import { Users, Snowflake, CheckCircle, ChevronDown, ChevronUp, Clock, MapPin, Gauge, Banknote, PlusCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { vehicles } from '@/data/vehicles';
import { motion } from 'framer-motion';

const PHONE_NUMBER = '919633210606';

const VehicleRow = ({ vehicle, index }: { vehicle: typeof vehicles[0]; index: number }) => {
  const isEven = index % 2 === 0;

  // Helper to get a description based on vehicle type
  const getVehicleDescription = (name: string) => {
    if (name.includes('Innova')) return "Experience premium comfort with our spacious Innova fleet, perfect for families and long-distance travel.";
    if (name.includes('Etios')) return "Reliable and economical choice for small families and business trips across Kerala.";
    if (name.includes('Tempo') || name.includes('Urbania')) return "The ultimate solution for large groups, combining luxury seating with ample luggage space.";
    if (name.includes('Bus')) return "Ideal for large tour groups, weddings, and corporate events with maximum comfort.";
    return "Reliable and comfortable transport service for your journey.";
  };

  const handleBookVehicle = (vehicleName: string) => {
    const message = `Hello, I want to book a ${vehicleName} from Friends On Road Cab, Kannur.`;
    window.open(
      `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`flex flex-col-reverse lg:flex-row items-center gap-6 lg:gap-10 group ${!isEven ? 'lg:flex-row-reverse' : ''}`}
    >
      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 w-full lg:w-1/2"
      >
        <div className="relative">
          <span className="inline-block py-1 px-2.5 rounded-full bg-primary/10 text-primary text-[10px] sm:text-xs font-bold tracking-wider uppercase mb-2">
            {vehicle.suitable}
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors duration-300">
            {vehicle.name}
          </h3>
          <div className="h-1 w-16 bg-primary/30 rounded-full mb-4 group-hover:w-24 transition-all duration-300" />

          <p className="text-gray-600 text-base leading-relaxed mb-4">
            {getVehicleDescription(vehicle.name)}
          </p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-3 text-gray-700">
              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <CheckCircle className="h-3 w-3 text-primary" />
              </div>
              <span className="text-sm font-medium">{vehicle.seats} Seating Capacity</span>
            </div>
            {vehicle.ac && (
              <div className="flex items-center gap-3 text-gray-700">
                <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <CheckCircle className="h-3 w-3 text-primary" />
                </div>
                <span className="text-sm font-medium">Air Conditioned</span>
              </div>
            )}
          </div>

          {/* Expanded Details */}
          {(vehicle.details || vehicle.packages) && (
            <div className="mb-4">
              {vehicle.details && (
                <div className="grid grid-cols-2 gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Duration</span>
                    <span className="font-bold text-gray-800 flex items-center gap-1.5 text-sm">
                      <Clock className="w-3.5 h-3.5 text-primary" /> {vehicle.details.duration}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Distance</span>
                    <span className="font-bold text-gray-800 flex items-center gap-1.5 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-primary" /> {vehicle.details.tariffKm} KM
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Extra KM</span>
                    <span className="font-bold text-gray-800 flex items-center gap-1.5 text-sm">
                      <Gauge className="w-3.5 h-3.5 text-primary" /> ₹{vehicle.details.extraKm}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Extra Hr</span>
                    <span className="font-bold text-gray-800 flex items-center gap-1.5 text-sm">
                      <PlusCircle className="w-3.5 h-3.5 text-primary" /> ₹{vehicle.details.extraHrs}
                    </span>
                  </div>
                </div>
              )}

              {vehicle.packages && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vehicle.packages.map((pkg, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <h4 className="text-sm font-bold text-gray-900 mb-2 pb-2 border-b border-gray-100 flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-primary" />
                        {pkg.name}
                      </h4>
                      <div className="grid grid-cols-2 gap-y-2 gap-x-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Duration</span>
                          <span className="font-bold text-gray-800 flex items-center gap-1.5 text-xs sm:text-sm">
                            <Clock className="w-3 h-3 text-primary" /> {pkg.duration}
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Tariff KM</span>
                          <span className="font-bold text-gray-800 flex items-center gap-1.5 text-xs sm:text-sm">
                            <MapPin className="w-3 h-3 text-primary" /> {pkg.tariffKm} KM
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Base Rate</span>
                          <span className="font-bold text-primary flex items-center gap-1.5 text-xs sm:text-sm">
                            <Banknote className="w-3 h-3" /> ₹{pkg.rate}
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Extra KM</span>
                          <span className="font-bold text-gray-800 flex items-center gap-1.5 text-xs sm:text-sm">
                            <Gauge className="w-3 h-3 text-primary" /> ₹{pkg.extraKm}
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5 col-span-2">
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Extra Hour</span>
                          <span className="font-bold text-gray-800 flex items-center gap-1.5 text-xs sm:text-sm">
                            <PlusCircle className="w-3 h-3 text-primary" /> ₹{pkg.extraHrs}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={() => handleBookVehicle(vehicle.name)}
              className="bg-gray-900 hover:bg-primary text-white text-sm font-semibold px-6 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            >
              Book Now
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Image Content */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? 50 : -50, scale: 0.9 }}
        whileInView={{ opacity: 1, x: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex-1 w-full lg:w-1/2 relative"
      >
        <div className="relative z-10 px-4 sm:px-0">
          {/* Abstract Background Blob - Reduced size */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-tr from-primary/5 to-blue-100/50 rounded-full blur-2xl -z-10" />

          <div className="relative group-hover:scale-105 transition-transform duration-700 ease-in-out">
            <img
              src={vehicle.image}
              alt={vehicle.name}
              className="w-full h-auto drop-shadow-xl object-contain relative z-10 max-h-[250px] sm:max-h-[300px] lg:max-h-[400px] mx-auto"
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const VehiclesSection = () => {
  return (
    <section id="vehicles" className="py-10 md:py-16 section-gradient-light overflow-hidden">
      <div className="container px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-bold text-primary uppercase tracking-widest mb-2"
          >
            OUR FLEET
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-3"
          >
            Choose Your <span className="text-primary">Perfect Ride</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base"
          >
            Choose from our wide range of well-maintained vehicles for a comfortable and safe journey.
          </motion.p>
        </div>

        {/* Vehicles List */}
        <div className="flex flex-col gap-10 lg:gap-14 max-w-7xl mx-auto">
          {vehicles.map((vehicle, index) => (
            <VehicleRow key={vehicle.id} vehicle={vehicle} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VehiclesSection;
